import { useRef, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import Waveform from "@/components/Waveform";
import { useAuth, formatApiError } from "@/lib/AuthContext";
import { inputCls, inputClsError, Btn, PasswordField, PasswordRules, passwordRules } from "@/dashboard/ui";

const NAME_RE = /^[\p{L}][\p{L}\s'’.-]*$/u;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validName(v) {
  const t = v.trim();
  return t.length > 0 && t.length <= 80 && NAME_RE.test(t);
}

export default function Login({ initialMode = "signin" }) {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldErr, setFieldErr] = useState({});
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const consentRef = useRef(null);

  if (user) return <Navigate to="/dashboard" replace />;

  const setErr = (field, message) =>
    setFieldErr((prev) => ({ ...prev, [field]: message }));

  const clearErr = (field) =>
    setFieldErr((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const switchMode = () => {
    // Never carry values between forms — nothing typed into signup may appear
    // in the sign-in form (and passwords are never carried in either direction).
    setMode(mode === "signin" ? "register" : "signin");
    setError("");
    setFieldErr({});
    setName("");
    setEmail("");
    setPassword("");
    setConfirm("");
    setConsent(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return; // guard against double submission
    setError("");
    const errs = {};
    if (mode === "register") {
      if (!name.trim()) errs.name = "Please enter your name.";
      else if (!validName(name)) errs.name = "Please enter a valid name.";
    }
    if (!email.trim()) errs.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(email.trim())) errs.email = "Please enter a valid email address.";
    if (!password) errs.password = "Please enter your password.";
    if (mode === "register") {
      if (password && !passwordRules.every((r) => r.test(password))) {
        errs.password = "Please meet all the password requirements below.";
      }
      if (confirm !== password) errs.confirm = "Passwords do not match.";
      if (!consent) errs.consent = "Please agree to the Terms of Service and Privacy Policy to continue.";
    }
    setFieldErr(errs);
    if (Object.keys(errs).length > 0) {
      // Move focus to the first invalid field
      const order = mode === "register"
        ? ["name", "email", "password", "confirm", "consent"]
        : ["email", "password"];
      const first = order.find((k) => errs[k]);
      const refs = { name: nameRef, email: emailRef, consent: consentRef };
      refs[first]?.current?.focus();
      if (first === "password" || first === "confirm") {
        document.querySelector(`[data-testid="${first === "password" ? "login-password-input" : "register-confirm-input"}"]`)?.focus();
      }
      return;
    }
    setBusy(true);
    try {
      if (mode === "signin") await login(email.trim(), password);
      else await register(name.trim(), email.trim(), password, consent);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.12)]">
          <Link to="/" className="flex items-center justify-center gap-2.5" data-testid="login-brand">
            <BrandLogo />
            <span className="text-lg font-bold tracking-tight text-[#111111]">Savy Kerno</span>
          </Link>
          <div className="mx-auto mt-4 w-40 opacity-80">
            <Waveform compact />
          </div>
          <h1 className="mt-6 text-center text-xl font-extrabold tracking-tight text-[#111111]">
            {mode === "signin" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-1.5 text-center text-sm text-neutral-500">
            {mode === "signin" ? "Sign in to your dashboard" : "Start organising your leads today"}
          </p>

          <form onSubmit={submit} noValidate className="mt-7 space-y-4" data-testid="login-form">
            {mode === "register" && (
              <div>
                <label htmlFor="register-name" className="mb-1.5 block text-xs font-semibold text-neutral-600">Your name</label>
                <input
                  id="register-name"
                  ref={nameRef}
                  data-testid="register-name-input"
                  className={fieldErr.name ? inputClsError : inputCls}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (fieldErr.name && validName(e.target.value)) clearErr("name");
                  }}
                  onBlur={() => {
                    if (name.trim() && !validName(name)) setErr("name", "Please enter a valid name.");
                  }}
                  placeholder="Alex Morgan"
                  aria-invalid={!!fieldErr.name || undefined}
                  aria-describedby={fieldErr.name ? "register-name-error" : undefined}
                  autoComplete="name"
                />
                {fieldErr.name && (
                  <p id="register-name-error" role="alert" data-testid="register-name-error" className="mt-1.5 text-xs text-red-600">
                    {fieldErr.name}
                  </p>
                )}
              </div>
            )}
            <div>
              <label htmlFor="login-email" className="mb-1.5 block text-xs font-semibold text-neutral-600">Email</label>
              <input
                id="login-email"
                ref={emailRef}
                data-testid="login-email-input"
                type="email"
                className={fieldErr.email ? inputClsError : inputCls}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErr.email && EMAIL_RE.test(e.target.value.trim())) clearErr("email");
                }}
                onBlur={() => {
                  if (email.trim() && !EMAIL_RE.test(email.trim())) setErr("email", "Please enter a valid email address.");
                }}
                placeholder="you@business.com"
                aria-invalid={!!fieldErr.email || undefined}
                aria-describedby={fieldErr.email ? "login-email-error" : undefined}
                autoComplete="email"
              />
              {fieldErr.email && (
                <p id="login-email-error" role="alert" data-testid="login-email-error" className="mt-1.5 text-xs text-red-600">
                  {fieldErr.email}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="login-password" className="mb-1.5 block text-xs font-semibold text-neutral-600">Password</label>
              <PasswordField
                testid="login-password-input"
                inputId="login-password"
                value={password}
                invalid={!!fieldErr.password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErr.password && e.target.value) clearErr("password");
                }}
                placeholder={mode === "register" ? "14+ characters" : "Your password"}
              />
              {fieldErr.password && (
                <p role="alert" data-testid="login-password-error" className="mt-1.5 text-xs text-red-600">
                  {fieldErr.password}
                </p>
              )}
              {mode === "signin" && (
                <div className="mt-2 text-right">
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-xs font-medium text-neutral-500 transition-colors hover:text-[#111111]"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>
            {mode === "register" && (
              <>
                <PasswordRules password={password} />
                <div>
                  <label htmlFor="register-confirm" className="mb-1.5 block text-xs font-semibold text-neutral-600">Confirm password</label>
                  <PasswordField
                    testid="register-confirm-input"
                    inputId="register-confirm"
                    value={confirm}
                    invalid={!!fieldErr.confirm}
                    onChange={(e) => {
                      setConfirm(e.target.value);
                      if (e.target.value === password) clearErr("confirm");
                    }}
                    placeholder="Repeat your password"
                  />
                  {(fieldErr.confirm || (confirm && password !== confirm)) && (
                    <p role="alert" data-testid="confirm-mismatch" className="mt-1.5 text-xs text-red-600">
                      Passwords do not match.
                    </p>
                  )}
                </div>
                <div>
                  <label className={`flex items-start gap-3 rounded-xl border p-3.5 ${fieldErr.consent ? "border-red-500" : "border-neutral-200"}`} data-testid="consent-row">
                    <input
                      ref={consentRef}
                      type="checkbox"
                      checked={consent}
                      data-testid="register-consent-checkbox"
                      aria-invalid={!!fieldErr.consent || undefined}
                      aria-describedby={fieldErr.consent ? "consent-error" : undefined}
                      onChange={(e) => {
                        setConsent(e.target.checked);
                        if (e.target.checked) clearErr("consent");
                      }}
                      className="mt-0.5 h-4 w-4 accent-neutral-900"
                    />
                    <span className="text-xs leading-relaxed text-neutral-600">
                      I agree to the{" "}
                      <Link to="/terms" target="_blank" data-testid="consent-terms-link" className="font-semibold underline underline-offset-2 hover:text-[#111111]">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy" target="_blank" data-testid="consent-privacy-link" className="font-semibold underline underline-offset-2 hover:text-[#111111]">
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </label>
                  {fieldErr.consent && (
                    <p id="consent-error" role="alert" data-testid="consent-error" className="mt-1.5 text-xs text-red-600">
                      {fieldErr.consent}
                    </p>
                  )}
                </div>
              </>
            )}
            {error && (
              <p data-testid="login-error" role="alert" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">
                {error}
              </p>
            )}
            <Btn
              data-testid={mode === "signin" ? "login-submit-button" : "register-submit-button"}
              type="submit"
              disabled={busy}
              className="w-full py-3"
            >
              {busy ? "Please wait…" : mode === "signin" ? "Sign In" : "Create Account"}
            </Btn>
          </form>

          <button
            data-testid="login-mode-toggle"
            onClick={switchMode}
            className="mt-5 w-full text-center text-sm font-medium text-neutral-500 hover:text-[#111111]"
          >
            {mode === "signin" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
        </div>
        <p className="mt-6 text-center text-xs text-neutral-400">
          <Link to="/" className="hover:text-neutral-600">← Back to savykernoai.com</Link>
        </p>
      </div>
    </div>
  );
}
